import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AutoSizer } from "react-virtualized";
import { useVirtualizer } from "@tanstack/react-virtual";
import PropTypes from "prop-types";
import { MySubtaskContainer } from "~/components/MyWork/MyTasks/MySubtaskContainer";
import UserWorkListHeader from "~/components/MyWork/MyTasks/UserWorkListHeader";
import { UserWorkTaskItem } from "~/components/MyWork/MyTasks/UserWorkTaskItem";
import TeamProjectItem from "~/pages/People/Teams/TeamProjectItem";
import TeamWorkListHeader from "~/pages/People/Teams/TeamWorkListHeader";
import { editPersonalizedConfigOptions } from "~/store/actions/actions.personalizedConfigOptions";
import { getMyWorkCollapsedProjects, getMyWorkCollapsedTaskLists } from "~/store/selectors/selector.configOptions";
import { makeGetProjectsForVirtualizedList } from "~/store/selectors/selector.userTasks";
import { TeamTaskListItem } from "./TeamTaskListItem";

export const TeamTasksGroupedByProjectList = ({ membersIds, isForUserProfile }) => {
  const parentRef = useRef();
  const dispatch = useDispatch();
  const collapsedProjectsFromConfig = useSelector(getMyWorkCollapsedProjects);
  const collapsedTaskListsFromConfig = useSelector(getMyWorkCollapsedTaskLists);
  const [collapsedProjects, setCollapsedProjects] = useState(membersIds ? [] : collapsedProjectsFromConfig);
  const [collapsedTaskLists, setCollapsedTaskLists] = useState(membersIds ? [] : collapsedTaskListsFromConfig);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const getVirtualizedWorkProjects = useMemo(() => makeGetProjectsForVirtualizedList(membersIds), [membersIds]);
  const virtualizedListProjectsData = useSelector(getVirtualizedWorkProjects);

  const data = useMemo(
    () =>
      virtualizedListProjectsData.filter((row) => {
        if (row.type === "project") {
          return true;
        }

        if (row.type === "list") {
          return !collapsedProjects.includes(row.list.project_id);
        }

        if (row.type === "task" || row.type === "subtask") {
          return (
            !collapsedProjects.includes(row.task?.project_id || row.subtask?.project_id) &&
            !collapsedTaskLists.includes(row.task?.task_list_id || row.subtask?.task_list_id)
          );
        }
      }),
    [collapsedProjects, collapsedTaskLists, virtualizedListProjectsData]
  );

  const handleCollapsedProjects = useCallback(
    (id) => {
      setCollapsedProjects((prevState) =>
        prevState.includes(id) ? prevState.filter((p) => p !== id) : [...prevState, id]
      );
      if (!membersIds) {
        let value;
        if (collapsedProjectsFromConfig.includes(id)) {
          value = collapsedProjectsFromConfig.filter((projectId) => projectId !== id);
        } else {
          value = [...collapsedProjectsFromConfig, id];
        }
        dispatch(editPersonalizedConfigOptions({ my_work_collapsed_projects: value }));
      }
    },
    [collapsedProjectsFromConfig, dispatch, membersIds]
  );

  const handleCollapsedTaskLists = useCallback(
    (id) => {
      setCollapsedTaskLists((prevState) =>
        prevState.includes(id) ? prevState.filter((p) => p !== id) : [...prevState, id]
      );
      if (!membersIds) {
        let value;
        if (collapsedTaskListsFromConfig.includes(id)) {
          value = collapsedTaskListsFromConfig.filter((lid) => lid !== id);
        } else {
          value = [...collapsedTaskListsFromConfig, id];
        }
        dispatch(editPersonalizedConfigOptions({ my_work_collapsed_task_lists: value }));
      }
    },
    [collapsedTaskListsFromConfig, dispatch, membersIds]
  );

  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 25,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    if (virtualItems.length) {
      setHasScrollbar(rowVirtualizer.getTotalSize() > rowVirtualizer.getSize());
    }
  }, [rowVirtualizer, virtualItems.length]);

  const renderItem = useCallback(
    (item) => {
      switch (item.type) {
        case "project": {
          return (
            <TeamProjectItem
              id={item.project.id}
              companyId={item.project.companyId}
              membersIds={membersIds}
              className="tw-mt-0"
              isExpanded={!collapsedProjects.includes(item.project.id)}
              handleCollapsedProjects={() => handleCollapsedProjects(item.project.id)}
            />
          );
        }
        case "list": {
          return (
            <TeamTaskListItem
              key={`task-list-group-${item.list.id}`}
              id={item.list.id}
              name={item.list.name}
              membersIds={membersIds}
              isExpanded={!collapsedTaskLists.includes(item.list.id)}
              handleCollapsedTaskLists={() => handleCollapsedTaskLists(item.list.id)}
            />
          );
        }
        case "task": {
          return (
            <div className="tasks_group_list">
              <UserWorkTaskItem
                id={item.task.id}
                showAssignee={!isForUserProfile}
                hideStopwatch={!!membersIds}
              />
            </div>
          );
        }
        case "subtask": {
          return (
            <div className="tasks_group_list">
              <div className="list-item-wrapper">
                <MySubtaskContainer
                  id={item.subtask.id}
                  className="tw-rounded-none tw-pl-6"
                  isAssigneeHidden={isForUserProfile}
                />
              </div>
            </div>
          );
        }
        default:
          return null;
      }
    },
    [
      collapsedProjects,
      collapsedTaskLists,
      handleCollapsedProjects,
      handleCollapsedTaskLists,
      isForUserProfile,
      membersIds,
    ]
  );

  const generateKey = useCallback((item) => {
    let key;

    switch (item?.type) {
      case "project":
        return (key = `project-${item?.project?.id}`);
      case "list":
        return (key = `list-${item?.list?.id}`);
      case "task":
        return (key = `task-${item?.task?.id}`);
      case "subtask":
        return (key = `subtask-${item?.subtask?.id}`);
      default:
        key = Math.random().toString(36);
    }

    return key;
  }, []);

  return (
    <>
      {membersIds ? (
        <TeamWorkListHeader hasScrollbar={hasScrollbar} />
      ) : (
        <UserWorkListHeader hasScrollbar={hasScrollbar} />
      )}
      <AutoSizer>
        {({ height, width }) => (
          <div
            ref={parentRef}
            style={{
              width,
              height,
              overflowY: "auto",
            }}
          >
            <div
              style={{
                height: rowVirtualizer.getTotalSize(),
                width: "100%",
                position: "relative",
              }}
            >
              {virtualItems.map((virtualRow) => {
                const item = data[virtualRow.index];
                return (
                  <div
                    key={generateKey(item)}
                    ref={rowVirtualizer.measureElement}
                    data-index={virtualRow.index}
                    style={{
                      position: "absolute",
                      width: "100%",
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {renderItem(item)}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </AutoSizer>
    </>
  );
};

TeamTasksGroupedByProjectList.propTypes = {
  membersIds: PropTypes.array,
  isForUserProfile: PropTypes.bool,
};

TeamTasksGroupedByProjectList.defaultProps = {
  isForUserProfile: false,
};
